import { AuthApiError, apiBaseUrl } from '../account/authApi';

/**
 * Public gallery endpoints + the kid's own submission status.
 *
 * `submitToGallery` creates a `pending` row server-side; the
 * moderation pipeline runs asynchronously and flips it to
 * `approved` / `rejected`. The studio polls `mySubmissions` to
 * show status badges.
 */

export interface GalleryEntry {
  id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  submitted_at: string;
  description: string | null;
  class_labels: string[];
  sibling_live_url: string | null;
  /** Server-computed: true when the signed-in user is the maker.
   *  Powers the inline "Edit description" affordance on the detail
   *  page without leaking submitter identity to other visitors. */
  is_mine: boolean;
  /** How many times this submission has been remixed. Bumped
   *  server-side on each `/gallery/:id/remix-bundle` fetch. */
  remix_count: number;
}

export interface GalleryPublishedAppEntry {
  id: string;
  title: string;
  slug: string;
  share_url: string;
  view_count: number;
  version: number;
  published_at: string;
  sibling_submission_id: string | null;
}

export interface GalleryPage {
  items: GalleryEntry[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export interface GalleryAppsPage {
  items: GalleryPublishedAppEntry[];
  total: number;
  has_more: boolean;
  limit: number;
  offset: number;
}

export type SubmissionStatus =
  | 'pending'
  | 'processing'
  | 'awaiting_parent_consent'
  | 'awaiting_teacher_review'
  | 'awaiting_moderator'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

export interface SubmissionSummary {
  id: string;
  project_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  status: SubmissionStatus;
  rejection_reason: string | null;
  submitted_at: string;
  updated_at: string;
}

export interface BlobUploadInput {
  classLabel: string;
  kind: 'image' | 'audio';
  /** Browser-supplied file or blob. The mime type is taken from
   *  this directly. */
  file: Blob;
  fileName: string;
}

export interface BlobSummary {
  id: string;
  project_id: string;
  class_label: string;
  kind: 'image' | 'audio';
  content_type: string;
  size_bytes: number;
  sha256: string;
  created_at: string;
}

export interface StorageUsage {
  used_bytes: number;
  quota_bytes: number;
}

export type ReportReason =
  | 'inappropriate-content'
  | 'personal-info'
  | 'harassment'
  | 'copyright'
  | 'spam'
  | 'other';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, { ...init, headers, credentials: 'include' });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Empty body — fall through.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function listGallery(
  params: { limit?: number; offset?: number } = {},
): Promise<GalleryPage> {
  const u = new URLSearchParams();
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<GalleryPage>(`/gallery${qs ? `?${qs}` : ''}`);
}

export async function listGalleryApps(
  params: { limit?: number; offset?: number } = {},
): Promise<GalleryAppsPage> {
  const u = new URLSearchParams();
  if (typeof params.limit === 'number') u.set('limit', String(params.limit));
  if (typeof params.offset === 'number') u.set('offset', String(params.offset));
  const qs = u.toString();
  return request<GalleryAppsPage>(`/gallery/apps${qs ? `?${qs}` : ''}`);
}

export async function getGalleryEntry(id: string): Promise<GalleryEntry> {
  return request<GalleryEntry>(`/gallery/${encodeURIComponent(id)}`);
}

export async function updateMyDescription(id: string, description: string): Promise<GalleryEntry> {
  return request<GalleryEntry>(`/gallery/${encodeURIComponent(id)}/description`, {
    method: 'PATCH',
    body: JSON.stringify({ description: description.trim() || null }),
  });
}

export async function submitToGallery(
  projectId: string,
  description?: string,
): Promise<SubmissionSummary> {
  return request<SubmissionSummary>('/gallery/submit', {
    method: 'POST',
    body: JSON.stringify({
      project_id: projectId,
      ...(description ? { description } : {}),
    }),
  });
}

export async function listMySubmissions(): Promise<SubmissionSummary[]> {
  return request<SubmissionSummary[]>('/gallery/me/submissions');
}

/** Take down one of your own gallery submissions. Idempotent on the
 *  server — calling on an already-withdrawn row returns the same
 *  summary unchanged. Strangers get a 404. */
export async function withdrawSubmission(id: string): Promise<SubmissionSummary> {
  return request<SubmissionSummary>(`/gallery/me/submissions/${encodeURIComponent(id)}/withdraw`, {
    method: 'POST',
  });
}

export async function uploadBlob(projectId: string, input: BlobUploadInput): Promise<BlobSummary> {
  // Multipart form data — `request` only knows JSON, so we hit
  // `fetch` directly here. We do not set a Content-Type header;
  // letting fetch infer the boundary keeps the multipart envelope
  // valid.
  const form = new FormData();
  form.append('class_label', input.classLabel);
  form.append('kind', input.kind);
  // JSDOM's FormData rejects raw Blobs — wrap in File for portability.
  // Real browsers accept either, so this is a no-cost compatibility
  // shim. The mime type carries through from the source blob.
  const fileObj =
    input.file instanceof File
      ? input.file
      : new File([input.file], input.fileName, { type: input.file.type });
  form.append('file', fileObj);
  const response = await fetch(`${apiBaseUrl()}/projects/${encodeURIComponent(projectId)}/blobs`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON / empty — fall through.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(response.status, detail, `Upload failed (${response.status}).`);
  }
  return body as BlobSummary;
}

export async function getStorageUsage(): Promise<StorageUsage> {
  return request<StorageUsage>('/storage/usage');
}

export interface RemixBundle {
  submission_id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  block_program: Record<string, unknown> | null;
  class_labels: string[];
}

export async function getRemixBundle(submissionId: string): Promise<RemixBundle> {
  return request<RemixBundle>(`/gallery/${encodeURIComponent(submissionId)}/remix-bundle`);
}

export async function reportGalleryEntry(
  id: string,
  reason: ReportReason,
  detail?: string,
): Promise<{ status: string }> {
  const body: Record<string, unknown> = { reason };
  if (detail) body.detail = detail;
  return request<{ status: string }>(`/gallery/${encodeURIComponent(id)}/report`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
